<template>
  <div>
    <NavBar />
    <main class="page marches-page">
      <header class="page-header marches-hero">
        <div>
          <p class="page-kicker">Organisation marchés</p>
          <h1 class="page-title">Lieux de marché</h1>
          <p class="page-subtitle">Gérez les communes officielles et les libellés utilisés pour vos marchés.</p>
        </div>
        <button class="btn btn-secondary reload-btn" @click="charger" :disabled="chargement">
          {{ chargement ? 'Chargement...' : 'Actualiser' }}
        </button>
      </header>

      <section class="card form-card">
        <div class="section-heading">
          <div>
            <p class="section-kicker">Nouveau lieu</p>
            <h2>Ajouter un lieu de marché</h2>
          </div>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label class="label">Nom affichage *</label>
            <input v-model.trim="form.nom_affichage" class="input" placeholder="Ex: Sète - Halles" />
          </div>
          <div class="form-group postal-group">
            <label class="label">Nom ou code postal *</label>
            <div class="search-row">
              <input
                v-model.trim="rechercheCommune"
                class="input"
                placeholder="Sète ou 34200"
                @keyup.enter="rechercherCommunes"
              />
              <button class="btn btn-primary search-btn" @click="rechercherCommunes" :disabled="rechercheEnCours || !rechercheCommune">
                {{ rechercheEnCours ? 'Recherche...' : 'Rechercher' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="communes.length" class="communes-list">
          <button
            v-for="commune in communes"
            :key="communeKey(commune)"
            type="button"
            class="commune-option"
            :class="{ selected: communeSelectionnee && communeKey(communeSelectionnee) === communeKey(commune) }"
            @click="selectionnerCommune(commune)"
          >
            <span>
              <strong>{{ commune.nom }} — {{ libelleCodesPostaux(commune) }}</strong>
              <small>INSEE {{ commune.code_insee }}</small>
            </span>
            <span class="select-indicator">Sélectionner</span>
          </button>
        </div>

        <div class="selected-grid" v-if="communeSelectionnee">
          <div>
            <span class="meta-label">Ville</span>
            <strong>{{ form.ville }}</strong>
          </div>
          <div>
            <span class="meta-label">Code INSEE</span>
            <strong>{{ form.code_insee }}</strong>
          </div>
          <div>
            <span class="meta-label">Latitude</span>
            <strong>{{ form.latitude || '—' }}</strong>
          </div>
          <div>
            <span class="meta-label">Longitude</span>
            <strong>{{ form.longitude || '—' }}</strong>
          </div>
        </div>

        <p v-if="erreurForm" class="message error">{{ erreurForm }}</p>
        <p v-if="succesForm" class="message success">{{ succesForm }}</p>

        <button class="btn btn-primary submit-btn" @click="creerLieu" :disabled="creationEnCours">
          {{ creationEnCours ? 'Création...' : 'Ajouter le lieu' }}
        </button>
      </section>

      <div v-if="chargement" class="card state-card">
        <span class="loading-dot"></span>
        <p>Chargement des lieux...</p>
      </div>

      <div v-else-if="erreur" class="card state-card error-state">
        <h3>Impossible de charger les lieux</h3>
        <p>{{ erreur }}</p>
        <button class="btn btn-secondary retry-btn" @click="charger">Réessayer</button>
      </div>

      <section v-else class="card list-card">
        <div class="section-heading list-heading">
          <div>
            <p class="section-kicker">Répertoire</p>
            <h2>Lieux enregistrés</h2>
          </div>
          <span class="count-pill">{{ lieux.length }} lieu{{ lieux.length > 1 ? 'x' : '' }}</span>
        </div>

        <div v-if="!lieux.length" class="empty-state">
          <h3>Aucun lieu</h3>
          <p>Ajoutez un premier lieu avec son code postal, puis sélectionnez la commune officielle.</p>
        </div>

        <div v-else class="lieux-list">
          <article v-for="lieu in lieux" :key="lieu.id" class="lieu-item" :class="{ inactive: !estActif(lieu) }">
            <div class="lieu-main">
              <div class="lieu-title-row">
                <h3>{{ lieu.nom_affichage }}</h3>
                <span class="badge" :class="estActif(lieu) ? 'badge-green' : 'badge-gray'">
                  {{ estActif(lieu) ? 'Actif' : 'Désactivé' }}
                </span>
              </div>
              <div class="lieu-meta">
                <span><strong>Ville</strong>{{ lieu.ville || '—' }}</span>
                <span><strong>Code postal</strong>{{ lieu.code_postal || '—' }}</span>
                <span><strong>Code INSEE</strong>{{ lieu.code_insee || '—' }}</span>
                <span><strong>Zone scolaire</strong>{{ lieu.zone_scolaire || '—' }}</span>
                <span><strong>Jours fériés</strong>{{ lieu.zone_jours_feries || '—' }}</span>
              </div>
            </div>
            <div class="lieu-actions">
              <button class="btn btn-secondary" @click="ouvrirEdition(lieu)">Éditer</button>
              <button class="btn btn-danger" @click="desactiverLieu(lieu)" :disabled="!estActif(lieu) || suppressionId === lieu.id">
                {{ suppressionId === lieu.id ? 'Désactivation...' : 'Désactiver' }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </main>

    <div class="modal-overlay" v-if="editionOuverte" @click.self="fermerEdition">
      <div class="modal">
        <div class="modal-heading">
          <p class="section-kicker">Edition</p>
          <h3>Modifier le lieu</h3>
        </div>
        <div class="form-group">
          <label class="label">Nom affichage</label>
          <input v-model.trim="edition.nom_affichage" class="input" />
        </div>
        <label class="active-toggle">
          <input v-model="edition.actif" type="checkbox" />
          <span>Lieu actif</span>
        </label>
        <p v-if="erreurEdition" class="message error">{{ erreurEdition }}</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="fermerEdition">Annuler</button>
          <button class="btn btn-primary" @click="sauvegarderEdition" :disabled="editionEnCours">
            {{ editionEnCours ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api from '../api'

export default {
  name: 'MarchesLieux',
  components: { NavBar },
  data() {
    return {
      lieux: [],
      chargement: false,
      erreur: '',
      erreurForm: '',
      succesForm: '',
      communes: [],
      communeSelectionnee: null,
      rechercheCommune: '',
      rechercheEnCours: false,
      creationEnCours: false,
      suppressionId: null,
      editionOuverte: false,
      editionEnCours: false,
      erreurEdition: '',
      form: this.formVide(),
      edition: {
        id: null,
        nom_affichage: '',
        actif: true
      }
    }
  },
  methods: {
    formVide() {
      return {
        nom_affichage: '',
        code_postal: '',
        ville: '',
        code_insee: '',
        latitude: null,
        longitude: null
      }
    },
    estActif(lieu) {
      return lieu.actif !== false
    },
    normalizeLieux(data) {
      if (Array.isArray(data)) return data
      return data?.lieux || data?.data || []
    },
    normalizeCommunes(data) {
      if (Array.isArray(data)) return data
      return data?.communes || data?.data || []
    },
    communeKey(commune) {
      return `${commune.code_insee || commune.code || commune.nom}-${this.codePostalCommune(commune)}`
    },
    codePostalCommune(commune) {
      if (commune.code_postal_principal) return commune.code_postal_principal
      if (commune.code_postal) return commune.code_postal
      if (Array.isArray(commune.codes_postaux)) return commune.codes_postaux[0] || ''
      return ''
    },
    libelleCodesPostaux(commune) {
      const codes = Array.isArray(commune.codes_postaux) ? commune.codes_postaux : []
      if (codes.length) return codes.join(' / ')
      return commune.code_postal || commune.code_postal_principal || '—'
    },
    async charger() {
      this.chargement = true
      this.erreur = ''
      try {
        const res = await api.get('/marches-lieux')
        this.lieux = this.normalizeLieux(res.data)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Erreur serveur'
      } finally {
        this.chargement = false
      }
    },
    async rechercherCommunes() {
      this.erreurForm = ''
      this.succesForm = ''
      this.communes = []
      this.communeSelectionnee = null
      const q = (this.rechercheCommune || '').trim()
      if (!q) {
        this.erreurForm = 'Nom ou code postal obligatoire'
        return
      }
      if (!/^\d{5}$/.test(q) && q.length < 2) {
        this.erreurForm = 'Recherche trop courte'
        return
      }
      this.rechercheEnCours = true
      try {
        const res = await api.get('/marches-lieux/search-communes', {
          params: { q }
        })
        this.communes = this.normalizeCommunes(res.data)
        if (!this.communes.length) this.erreurForm = 'Aucune commune trouvée'
      } catch (err) {
        this.erreurForm = err.response?.data?.error || 'Recherche commune impossible'
      } finally {
        this.rechercheEnCours = false
      }
    },
    selectionnerCommune(commune) {
      const codePostal = this.codePostalCommune(commune)
      this.communeSelectionnee = commune
      this.form.ville = commune.nom || commune.ville || ''
      this.form.code_postal = codePostal
      this.form.code_insee = commune.code_insee || commune.code || ''
      this.form.latitude = commune.latitude ?? null
      this.form.longitude = commune.longitude ?? null
      this.rechercheCommune = this.form.ville
      if (!this.form.nom_affichage) this.form.nom_affichage = this.form.ville
    },
    async creerLieu() {
      this.erreurForm = ''
      this.succesForm = ''
      if (!this.form.nom_affichage || !this.form.code_postal || !this.form.ville || !this.form.code_insee) {
        this.erreurForm = 'Nom affichage, code postal et commune officielle sont obligatoires'
        return
      }
      this.creationEnCours = true
      try {
        await api.post('/marches-lieux', {
          nom_affichage: this.form.nom_affichage,
          ville: this.form.ville,
          code_postal: this.form.code_postal,
          code_insee: this.form.code_insee,
          latitude: this.form.latitude,
          longitude: this.form.longitude,
          zone_jours_feries: 'metropole',
          zone_scolaire: 'C'
        })
        this.succesForm = 'Lieu créé'
        this.form = this.formVide()
        this.rechercheCommune = ''
        this.communes = []
        this.communeSelectionnee = null
        await this.charger()
      } catch (err) {
        this.erreurForm = err.response?.data?.error || 'Création impossible'
      } finally {
        this.creationEnCours = false
      }
    },
    ouvrirEdition(lieu) {
      this.erreurEdition = ''
      this.edition = {
        id: lieu.id,
        nom_affichage: lieu.nom_affichage || '',
        actif: this.estActif(lieu)
      }
      this.editionOuverte = true
    },
    fermerEdition() {
      this.editionOuverte = false
      this.erreurEdition = ''
    },
    async sauvegarderEdition() {
      this.erreurEdition = ''
      if (!this.edition.nom_affichage) {
        this.erreurEdition = 'Le nom affichage est obligatoire'
        return
      }
      this.editionEnCours = true
      try {
        await api.patch(`/marches-lieux/${this.edition.id}`, {
          nom_affichage: this.edition.nom_affichage,
          actif: this.edition.actif
        })
        this.editionOuverte = false
        await this.charger()
      } catch (err) {
        this.erreurEdition = err.response?.data?.error || 'Modification impossible'
      } finally {
        this.editionEnCours = false
      }
    },
    async desactiverLieu(lieu) {
      if (!confirm(`Désactiver "${lieu.nom_affichage}" ?`)) return
      this.suppressionId = lieu.id
      try {
        await api.delete(`/marches-lieux/${lieu.id}`)
        lieu.actif = false
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Désactivation impossible'
      } finally {
        this.suppressionId = null
      }
    }
  },
  mounted() {
    this.charger()
  }
}
</script>

<style scoped>
.marches-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 20px 16px 44px;
}

.marches-hero {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 18px;
}

.page-kicker,
.section-kicker {
  margin: 0 0 6px;
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.page-title {
  margin: 0;
  color: var(--fm-navy);
  font-size: 30px;
  font-weight: 800;
}

.page-subtitle {
  max-width: 620px;
  margin: 8px 0 0;
  color: var(--fm-muted);
}

.reload-btn,
.submit-btn,
.retry-btn {
  width: 100%;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.section-heading h2 {
  margin: 0;
  font-size: 21px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.search-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.search-btn {
  min-height: 52px;
  padding-left: 14px;
  padding-right: 14px;
}

.communes-list {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}

.commune-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 64px;
  padding: 12px 14px;
  border: 1.5px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: var(--fm-surface);
  color: var(--fm-text);
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
}

.commune-option.selected {
  border-color: var(--fm-accent);
  background: var(--fm-accent-soft);
}

.commune-option small,
.meta-label {
  display: block;
  margin-top: 3px;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 600;
}

.select-indicator {
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.selected-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 16px;
  padding: 14px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: #F8FAFC;
}

.message {
  margin-top: 12px;
}

.state-card {
  display: flex;
  align-items: center;
  gap: 10px;
}

.loading-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--fm-accent);
  box-shadow: 0 0 0 6px rgba(30, 111, 220, 0.12);
}

.error-state {
  display: block;
}

.error-state p {
  margin: 8px 0 14px;
}

.list-heading {
  align-items: center;
}

.count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 5px 11px;
  border-radius: 999px;
  background: var(--fm-accent-soft);
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.empty-state {
  padding: 18px 0 4px;
}

.empty-state h3 {
  margin: 0 0 6px;
}

.lieux-list {
  display: grid;
  gap: 12px;
}

.lieu-item {
  display: grid;
  gap: 14px;
  padding: 14px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: #FFFFFF;
}

.lieu-item.inactive {
  background: #F8FAFC;
}

.lieu-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.lieu-title-row h3 {
  margin: 0;
  font-size: 18px;
  overflow-wrap: anywhere;
}

.lieu-meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.lieu-meta span {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-height: 34px;
  padding: 7px 0;
  border-bottom: 1px solid #EEF2F7;
  color: var(--fm-text);
}

.lieu-meta strong {
  color: var(--fm-muted);
  font-size: 12px;
  text-transform: uppercase;
}

.lieu-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.lieu-actions .btn {
  min-height: 46px;
  padding: 11px 14px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: min(100%, 520px);
  padding: 20px;
  border-radius: 18px;
  background: var(--fm-surface);
  box-shadow: var(--fm-shadow-soft);
}

.modal-heading {
  margin-bottom: 16px;
}

.modal-heading h3 {
  margin: 0;
}

.active-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  margin-top: 12px;
  color: var(--fm-text);
  font-weight: 700;
  cursor: pointer;
}

.active-toggle input {
  width: 22px;
  height: 22px;
  accent-color: var(--fm-accent);
}

.modal-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 18px;
}

@media (min-width: 720px) {
  .marches-page {
    padding: 28px 20px 52px;
  }

  .marches-hero {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }

  .page-title {
    font-size: 34px;
  }

  .reload-btn {
    width: auto;
  }

  .form-grid {
    grid-template-columns: minmax(220px, 0.8fr) minmax(320px, 1.2fr);
  }

  .search-row {
    grid-template-columns: minmax(150px, 0.7fr) minmax(190px, 1fr);
  }

  .selected-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .lieu-item {
    grid-template-columns: minmax(0, 1fr) 156px;
    align-items: start;
  }

  .lieu-meta {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .lieu-meta span {
    display: block;
    border-bottom: 0;
    padding: 0;
  }

  .lieu-meta strong {
    display: block;
    margin-bottom: 4px;
  }

  .submit-btn {
    width: auto;
    margin-top: 16px;
  }

  .modal-overlay {
    align-items: center;
  }

  .modal-actions {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
